/**
 * Dexie schema — Family (CLAUDE.md §2 Golden Rule 4 offline read, §6 madde 11–12)
 *
 * Mirrors the 5 server tables the UI needs to read offline + a local-only
 * `outbox` queue for messages composed without internet.
 *
 * Why Dexie (not raw IDB):
 *   - Promise-based API (matches the rest of the codebase).
 *   - Schema versioning — `version(N).stores({...})` lets us migrate without
 *     wiping user data when fields evolve in Phase 2.
 *   - Compound indexes via `[conversation_id+created_at]` give us
 *     "messages-in-this-conversation, newest first" for free.
 *
 * Schema versioning policy (forward-compat):
 *   - v1 — initial schema (this file).
 *   - vN — when adding a column, add a new `version(N).stores({...}).upgrade(tx => ...)`
 *     block. NEVER mutate v1 in place. Old clients running v1 must continue
 *     to work after a v2 deploy until they reload.
 *   - Bumping the major schema (incompatible) requires a `Dexie.delete('family')`
 *     migration helper invoked once per user. Phase 1 alpha will not need this.
 *
 * Index notes:
 *   - `&id` = primary key + unique. Used everywhere the server has a UUID PK.
 *   - `[conversation_id+created_at]` powers the chat list query: `where('[conversation_id+created_at]').between(...)`
 *   - `client_msg_id` is indexed but NOT unique on `messages` — a row can be
 *     re-saved by sync from server. Uniqueness is enforced ONLY in `outbox`.
 *   - `localStatus` is indexed so the UI can quickly query "any pending sends?"
 *
 * Privacy: IndexedDB is per-origin, per-profile. No cross-account leakage.
 * No PII leaves the device through this layer. Sync layer enforces RLS via
 * the Supabase client (which uses the user's auth token).
 */

import Dexie, { type Table } from 'dexie';
import type {
  ConversationLocal,
  MemberLocal,
  MessageLocal,
  MessageTranslationLocal,
  OutboxItem,
  UserLocal,
} from './types';

class FamilyDB extends Dexie {
  // Tables — populated by `version().stores()` below.
  messages!: Table<MessageLocal, string>;
  message_translations!: Table<MessageTranslationLocal, string>;
  conversations!: Table<ConversationLocal, string>;
  conversation_members!: Table<MemberLocal, [string, string]>;
  users!: Table<UserLocal, string>;
  outbox!: Table<OutboxItem, number>;

  constructor() {
    super('family');

    // v1 — initial schema. See top-of-file note before bumping.
    this.version(1).stores({
      // Primary key (id), then conversation_id (FK), compound (conversation_id+created_at)
      // for chat list queries, client_msg_id for idempotency lookups, localStatus for
      // outbox-status filtering.
      messages:
        '&id, conversation_id, [conversation_id+created_at], client_msg_id, localStatus',

      // Primary key (id), message_id for fast "translations of this message" join,
      // compound for "did we already cache <msg, lang>?".
      message_translations: '&id, message_id, [message_id+target_lang]',

      conversations: '&id',

      // Compound primary key — matches the Postgres `(conversation_id, user_id)` PK.
      // Both columns indexed so we can list "members of conv X" or "convs of user Y".
      conversation_members: '[conversation_id+user_id], conversation_id, user_id',

      users: '&id',

      // Outbox: numeric auto-increment id, &client_msg_id (unique — refuse double
      // enqueue of same idempotency token), status for queue scans, conversation_id
      // for "show pending sends in this chat".
      outbox: '++id, &client_msg_id, status, conversation_id',
    });
  }
}

/**
 * Singleton — Dexie instances are heavy (open IDB connection). Construct lazily
 * so server-side tests / SSR contexts don't hit IndexedDB-not-defined errors.
 */
let cachedDb: FamilyDB | null = null;

export function getDb(): FamilyDB {
  if (!cachedDb) {
    cachedDb = new FamilyDB();
  }
  return cachedDb;
}

/** Re-exported for tests that need to reset state between cases. */
export async function _resetDbForTests(): Promise<void> {
  if (cachedDb) {
    await cachedDb.delete();
    cachedDb = null;
  }
}

export type { FamilyDB };
